import { useEffect, useRef, useState } from "react";
import RecordRTC,{ RecordRTCPromisesHandler } from "recordrtc";
import { recordDuration } from "src/shared/utils/record-functions";
import { useBoundStore } from "../store";
import toast from "react-hot-toast";
import { IndexedVideo } from "../store/qcasts/data";

import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import { isValidBlob } from "../shared/utils/helper";


interface UseRecorderConfig {
  totalNumberOfVideoPermitted?: number;
}


export interface UseRecorderReturn {
  mediaStream: MediaStream | null;
  startedRecord: boolean;
  circleCompleted: boolean;
  allVideosRecorded: number;
  blob: Blob | null;
  recorder: RecordRTCPromisesHandler | null;
  duration: string;
  timer: number;
  videos: IndexedVideo[];
  isPaused: boolean;
  isReady:boolean;
  
  videoRef: React.RefObject<HTMLVideoElement>;
  currentIndex: number;
  
  
  isFaceCentered: boolean;
  
  
  countDown: number;
  countInterval: number;
  
  
  checkPermissions: () => Promise<boolean>;
  start: (index: number) => Promise<void>;
  startRecord: () => Promise<void>;
  stop: () => Promise<void>;
  newVideo: (index: number) => Promise<void>;
  nextVideo: (index: number) => Promise<void>;
  queueNextVideo: () => Promise<void>;
  transcript: string;
  captureSnapshot: (canvasRef: React.RefObject<HTMLCanvasElement>) => Promise<string | undefined>;
  
  // Setters
  setMediaStream: (stream: MediaStream | null) => void;
 
  setCurrentIndex: (index: number) => void;
  setCountInterval: (interval: number) => void;
  setMaxDuration: (duration: number) => void;
  setStartedRecord: (status: boolean) => void;
  setIsPaused: (status: boolean) => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  forceInitialize: () => void;
  cleanupMediaStream: () => void;
  
}

const MAX_DURATION = 60;
const DEFAULT_COUNTDOWN = 1;


const useRecorder = ({ totalNumberOfVideoPermitted = 1 }: UseRecorderConfig = {}): UseRecorderReturn => {
  // Core state
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [recorder, setRecorder] = useState<RecordRTCPromisesHandler | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [startedRecord, setStartedRecord] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [maxDuration, setMaxDuration] = useState<number>(MAX_DURATION);
  const [circleCompleted, setCircleCompleted] = useState(false);
  const [allVideosRecorded, setAllVideosRecorded] = useState(0);
  
  // Timer & countdown state
  const [timer, setTimer] = useState(0);
  const [countDown, setCountDown] = useState(DEFAULT_COUNTDOWN);
  const [countInterval, setCountInterval] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<number>(0);
  
  // Video management state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videos, setVideos] = useState<IndexedVideo[]>([]);
  const [isFaceCentered, setIsFaceCentered] = useState(false);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasRun = useRef(false);
  
  // Store access
  const { recordedVideo, addRecordedVideo, setVideoTranscription } = useBoundStore();

  //const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

/*   const videoConstraints = isMobile
    ? { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { max: 15 } }
    : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { max: 30 } }; */

  const mimeType = "video/mp4"//isMobile ? "video/webm;codecs=vp8" : "video/mp4;codecs=vp9";
  const isMobileDevice=()=> {
    // Check if the device supports touch events
    const hasTouchSupport = 'ontouchstart' in window || 
                           navigator.maxTouchPoints > 0 ||
                           window.matchMedia("(pointer: coarse)").matches;
                           
    // Check for smaller screen size (typically mobile devices)
    const hasSmallScreen = window.matchMedia("(max-width: 768px)").matches;
    
    return hasTouchSupport && hasSmallScreen;
  }

  // Utility functions
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  /**
   * Initialize camera on component mount and clean up on unmount
   */
  useEffect(() => {
    const initializeCamera = async () => {
      await initializeMediaStream();
    };
    
    initializeCamera();
    
    return () => {
      cleanupMediaStream();
    };
  }, []);

  /**
   * Set up video element when media stream or recording index changes
   */
  useEffect(() => {
    if (!mediaStream) {
      return;
    }

    const setupVideoElement = setInterval(() => {
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((error) => {
            toast.error(`Error playing video: ${error.message}`);
            console.error("Error playing video:", error);
          });
        };
        clearInterval(setupVideoElement);
      }
    }, 100);
    
    return () => clearInterval(setupVideoElement);
  }, [currentIndex, mediaStream]);

  /**
   * Update videos state when recorded videos change
   */
  useEffect(() => {
    if (recordedVideo.length > 0) {
      const validVideos = recordedVideo.filter((item: IndexedVideo) => isValidBlob(item.video));
      setVideos(validVideos);
    }
  }, [recordedVideo]);

  /**
   * Start recording when countdown reaches zero
   */
  
  useEffect(() => {
   
    if (countDown < 1 && !hasRun.current) {
      hasRun.current = true;

      const handleCountdownComplete = async () => {
        clearInterval(countInterval);
        await startRecord();
      };

      handleCountdownComplete();
    }
  }, [countDown]);

  /**
   * Handle max duration reached
   */
  useEffect(() => {
    const handleMaxDuration = async () => {
      if (timer >= maxDuration) {
        if (totalNumberOfVideoPermitted - 1 === currentIndex) {
          await stop();
          
          setAllVideosRecorded(prev => prev + 1);
        } else {
          await queueNextVideo();
        }
      }
    };
    
    handleMaxDuration();
  }, [timer]);

  const queueNextVideo = async()=>{
    await stop();
    setCurrentIndex(currentIndex + 1);
    setStartedRecord(false);
    clearInterval(timerInterval);
    setTimerInterval(null);
    setTimer(0);
    setBlob(null);
    await initializeMediaStream();
    // await nextVideo(currentIndex + 1);
  }

  /**
   * Check camera permissions
   */
  const checkPermissions = async (): Promise<boolean> => {
    try {
      const permissions = await navigator.permissions.query({ name: "camera" as PermissionName });
      return permissions.state === "granted";
    } catch (error) {
      console.error("Error checking permissions:", error);
      return false;
    }
  };

  /**
   * Initialize media stream
   */
  const initializeMediaStream = async (): Promise<MediaStream | null> => {
    try {
      if (mediaStream && mediaStream.active)  {
        setIsReady(true)
        return mediaStream
      };
  
      await checkPermissions();
      const newMediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        // video: { width: 720, height: 720 },
        video: true
      });
  
      if (!newMediaStream.active) {
        toast.error("Media stream is inactive");
        console.error("Media stream is inactive");
        return null;
      }

      
      setMediaStream(newMediaStream);
      setIsReady(true)
      
      // if (videoRef.current) {
      //   videoRef.current.srcObject = newMediaStream;
      //   videoRef.current.onloadedmetadata = () => {
      //     videoRef.current?.play().catch((error) => {
      //       toast.error(`Error playing video: ${error.message}`);
      //       console.error("Error playing video:", error);
      //     });
      //   };
      // } else {
      //   console.warn("videoRef.current is null. Check rendering order.");
      // }
      
      return newMediaStream;
    } catch (error: any) {
      toast.error(`Error accessing media devices: ${error.message}`);
      console.error("Error accessing media devices:", error);
      return null;
    }
  };

  /**
   * Clean up media stream
   */
  const cleanupMediaStream = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.readyState === "live" && track.stop());
    }
    
    if (recorder) {
      recorder.destroy();
    }
    
    clearInterval(timerInterval);
    clearInterval(countInterval);
  };

  /**
   * Capture a snapshot from the video feed
   */
  const captureSnapshot = async (canvasRef: React.RefObject<HTMLCanvasElement>): Promise<string | undefined> => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      canvas.width = video.videoWidth || 720;
      canvas.height = video.videoHeight || 720;
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/png");
    }
  };

  /**
   * Start the countdown process before recording
   */
  const start = async (index: number) => {
    setCurrentIndex(index);
    setIsRecording(true);
    setCountDown(DEFAULT_COUNTDOWN);
    
    // Start countdown
    const interval = window.setInterval(() => {
      setCountDown(prevTime => prevTime - 1);
    }, 1000);
    
    setCountInterval(interval);
  };
  const forceInitialize = async () => {
    
    setStartedRecord(false);
    clearInterval(timerInterval);
    setTimerInterval(null);
    setTimer(0);
    setBlob(null);
    hasRun.current=false;
    await initializeMediaStream();
  }
  /**
   * Begin actual recording after countdown
   */
  const startRecord = async () => {
    try {
      let  stream=mediaStream
      if (!stream) {
        stream = await initializeMediaStream();
        if (!stream) return;
      }
      
      // Clear any existing intervals
      clearInterval(countInterval);
      clearInterval(timerInterval);
      setTimer(0);
      
      if (typeof window !== "undefined") {
        // Initialize recorder
        const rtcRecorder = new RecordRTCPromisesHandler(stream, {
          type: "video",
          mimeType: mimeType,
          disableLogs:false,
          recorderType: RecordRTC.MediaStreamRecorder
        });
        await rtcRecorder.startRecording();
     
        setRecorder(rtcRecorder);
        await SpeechRecognition.startListening();

        
        // Start timer
        const interval = window.setInterval(() => setTimer(prev => prev + 0.01), 10);
        setTimerInterval(interval);
        setStartedRecord(true);
      }
    } catch (error: any) {
      toast.error(error.message);
      console.error("Error starting recording:", error);
    }
  };

  /**
   * Stop recording and save the result
   */
  const stop = async () => {
    
    try {
      // Clear intervals
      clearInterval(timerInterval);
      clearInterval(countInterval);
      
      // Stop recording if active
      if (recorder) {
        setIsRecording(false);
       const recorderStatus=await recorder.getState()
       if(recorderStatus!="stopped"){
        try{await recorder.stopRecording()}catch(err){};
       }
        
        
        // Get the recorded blob
        const recordedBlob = await recorder.getBlob();
        const videoBlob = new Blob([recordedBlob], { type: 'video/mp4'})//isMobile ? 'video/webm' : 'video/mp4' });
        if(SpeechRecognition.getRecognitionManager().listening){
          await SpeechRecognition.stopListening();
        }
       
        // Save to store
        setBlob(videoBlob);
        console.log("vvv",currentIndex)
        await addRecordedVideo(currentIndex, videoBlob, timer);
        await setVideoTranscription({ [currentIndex]: transcript });
        resetTranscript();
        
        // Reset state
        setRecorder(null);
        setStartedRecord(false);
        setCountDown(DEFAULT_COUNTDOWN);
        hasRun.current=false;
        setCountInterval(0);
      }
      
      // Stop media stream
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
      
      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (error: any) {
      console.error("Error stopping recording:", error);
      toast.error(`Error stopping recording: ${error.message}`);
    }
  };

  /**
   * Start a completely new video
   */
  const newVideo = async (index: number) => {
    setTimer(0);
    setBlob(null);
    hasRun.current=false;
    await initializeMediaStream();
    await start(index);
  };

  /**
   * Continue to next video segment without destroying stream
   */
  const nextVideo = async (index: number) => {
    if (!recorder) {
      console.error("Recorder not initialized");
      return;
    }
    
    try {
      // Stop current recording segment
      await recorder.stopRecording();
      const recordedBlob = await recorder.getBlob();
      const videoBlob = new Blob([recordedBlob], { type: 'video/mp4' });
      
      // Save current segment
      await addRecordedVideo(currentIndex, videoBlob, timer);
      
      // Update index and prepare for next segment
      setCurrentIndex(index);
      
      // Reset timer
      clearInterval(timerInterval);
      setTimer(0);
      
      // Start new recording segment
      await recorder.startRecording();
      const interval = window.setInterval(() => setTimer(prev => prev  + 0.01), 10);
      setTimerInterval(interval);
      
      // Ensure video element is correctly set up
      if (videoRef.current && mediaStream) {
        videoRef.current.srcObject = mediaStream;
      }
      setStartedRecord(true);
    } catch (error: any) {
      console.error("Error transitioning to next video:", error);
      toast.error(`Error transitioning to next video: ${error.message}`);
    }
  };


  const pauseRecording = () => {
    if (recorder && !isPaused) {
      recorder.pauseRecording();
      setIsPaused(true);
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    }
  };

  const resumeRecording = () => {
    if (recorder && isPaused) {
      recorder.resumeRecording();
      setIsPaused(false);
      const interval = window.setInterval(() => setTimer(prev => prev + 0.01), 10);
      setTimerInterval(interval);
    }
  };

  return {
    // Core state
    mediaStream,
    setMediaStream,
    startedRecord,
    blob,
    recorder,
    duration: recordDuration(timer),
    timer,
    videos,
    circleCompleted,
    allVideosRecorded,
    
    // Video management
    videoRef,
    currentIndex,
    setCurrentIndex,
    setMaxDuration,
    isPaused,
    
    // Face detection
    isFaceCentered,
    
    // Countdown
    countDown,
    countInterval,
    setCountInterval,
    setStartedRecord,
    setIsPaused,
    
    
    // Actions
    checkPermissions,
    start,
    startRecord,
    stop,
    newVideo,
    nextVideo,
    queueNextVideo,
    captureSnapshot,
    pauseRecording,
    resumeRecording,
    isReady,
    transcript,
    forceInitialize,
    cleanupMediaStream
  };
};

export default useRecorder;